import { useContext, useEffect, useRef, useState } from 'react';
import {
  Box,
  Flex,
  Icon,
  // Chakra Popover is used here (not @kula-ai/components) because we need a
  // controlled, trigger-less announcement anchored to the nav item with a
  // one-time dismiss — kula-ai's Popover is hover/click-trigger oriented.
  Popover,
  PopoverAnchor,
  PopoverBody,
  PopoverContent,
  type PopoverContentProps,
} from '@chakra-ui/react';
import { XClose, InternalJobs as InternalJobsIcon } from '@kula-ai/icons-core';
import { NavLink } from './NavLink';
import { KText } from '@/ui-library/components';
import NavigationContext from '../context';

// One-time announcement: tells users that internal jobs now live in the
// Internal job board. Persisted via localStorage so it shows once and never
// again after the user dismisses it.
const VIEWED_KEY = 'viewed_internal_job_board';

// Extracted into a typed constant (instead of inlining on <PopoverContent />)
// because the element carries enough token-typed style props that adding one
// more inline tips TS over its "union type too complex to represent" limit.
const announcementCardStyles: PopoverContentProps = {
  width: '420px',
  // The stacking-relevant element is the `.chakra-popover__popper` wrapper,
  // which Chakra themes with zIndex 'popover' (1500) — above the modal
  // overlay/dialog (both zIndex 'modal' = 1400), so the card would bleed over
  // open modals. `rootProps` targets that wrapper; a base z-index keeps the
  // card above page content but below modals/overlays. (Setting zIndex on the
  // content alone is ignored — the wrapper's stacking context wins.)
  rootProps: { zIndex: 1 },
  bg: 'primary-25',
  border: '1px solid',
  borderColor: 'primary-400',
  borderRadius: '6px',
  boxShadow: '0px 12px 32px rgba(38, 34, 68, 0.12)',
  _focus: {
    outline: 'none',
    boxShadow: '0px 12px 32px rgba(38, 34, 68, 0.12)',
  },
  _focusVisible: { outline: 'none' },
  // Speech-bubble tail on the left edge pointing at the nav item. Chakra's
  // <PopoverArrow> only renders a 4-sided diamond, so we draw the tail
  // ourselves: a rotated square whose two outer edges carry the primary-400
  // border while its body (primary-25) masks the card's left border where the
  // tail joins.
  _before: {
    content: '""',
    position: 'absolute',
    left: '-6px',
    // Vertically centered: placement="right" centers the card on the nav item,
    // so the tail points straight at "Internal job board".
    top: '50%',
    width: '12px',
    height: '12px',
    bg: 'primary-25',
    borderLeft: '1px solid',
    borderBottom: '1px solid',
    borderColor: 'primary-400',
    transform: 'translateY(-50%) rotate(45deg)',
  },
};

interface InternalJobBoardNavLinkProps {
  icon: React.ElementType;
  link: string;
  title: string;
  testID?: string;
  isCollapsed?: boolean;
  // Lets callers fall back to a plain NavLink (no nudge) when not applicable.
  nudgeEnabled?: boolean;
}

export const InternalJobBoardNavLink = ({
  nudgeEnabled = true,
  ...navLinkProps
}: InternalJobBoardNavLinkProps) => {
  // Show until the user has dismissed it (flag === 'true'). Read once on mount.
  const [showNudge, setShowNudge] = useState(
    () => nudgeEnabled && localStorage.getItem(VIEWED_KEY) !== 'true'
  );
  const { isNavCollapsed } = useContext(NavigationContext) ?? {};
  const isFirstCollapseRun = useRef(true);

  useEffect(() => {
    // First visit: seed the flag as 'false' so it's explicitly tracked.
    if (nudgeEnabled && localStorage.getItem(VIEWED_KEY) === null) {
      localStorage.setItem(VIEWED_KEY, 'false');
    }
  }, [nudgeEnabled]);

  // The sidebar animates its width when collapsing/expanding (~0.25s), which
  // moves the nav anchor. Chakra's Popover (Popper) only repositions on
  // scroll/window-resize, so on its own it won't track the anchor mid-
  // transition — the card would snap at the end. On each toggle, pump resize
  // events across the transition window so Popper recomputes every frame and
  // the popover glides with the sidebar.
  useEffect(() => {
    if (!showNudge) return undefined;
    if (isFirstCollapseRun.current) {
      isFirstCollapseRun.current = false;
      return undefined;
    }
    let rafId = 0;
    let start: number | null = null;
    const DURATION = 320; // sidebar width transition (250ms) + small buffer
    const tick = (ts: number) => {
      if (start === null) start = ts;
      window.dispatchEvent(new Event('resize'));
      if (ts - start < DURATION) {
        rafId = requestAnimationFrame(tick);
      }
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [isNavCollapsed, showNudge]);

  const dismissNudge = () => {
    localStorage.setItem(VIEWED_KEY, 'true');
    setShowNudge(false);
  };

  if (!nudgeEnabled) {
    return <NavLink {...navLinkProps} />;
  }

  return (
    <Popover
      isOpen={showNudge}
      placement="right"
      closeOnBlur={false}
      autoFocus={false}
      returnFocusOnClose={false}
      gutter={12}
      // Keep a 12px gap from the viewport edges so the card never sticks to
      // the bottom of the page.
      modifiers={[{ name: 'preventOverflow', options: { padding: 12 } }]}
    >
      <PopoverAnchor>
        <Box>
          <NavLink {...navLinkProps} />
        </Box>
      </PopoverAnchor>
      <PopoverContent {...announcementCardStyles}>
        <PopoverBody p="12px">
          <Flex justifyContent="space-between" alignItems="flex-start" mb="8px">
            <Flex alignItems="center" gap="8px">
              <Icon
                as={InternalJobsIcon}
                boxSize="18px"
                color="secondary-700"
              />
              <KText
                fontSize="14px"
                fontWeight="medium"
                color="secondary-900"
                lineHeight="20px"
              >
                Internal jobs have a new home
              </KText>
            </Flex>
            <Icon
              as={XClose}
              boxSize="16px"
              color="secondary-900"
              cursor="pointer"
              flexShrink={0}
              onClick={dismissNudge}
              _hover={{ color: 'secondary-900' }}
              data-test-id="internal-job-board-nudge-close"
            />
          </Flex>
          <KText
            fontSize="13px"
            fontWeight="regular"
            color="secondary-700"
            lineHeight="20px"
            mb="8px"
          >
            You can now apply to internal jobs in the{' '}
            <KText as="span" fontWeight="semiBold">
              Internal job board
            </KText>
            . Head there to browse open roles and track your applications in one
            place.
          </KText>
          {/* <KText
            as="button"
            fontSize="13px"
            fontWeight="semiBold"
            color="primary-600"
            cursor="pointer"
            _hover={{ textDecoration: 'underline' }}
            onClick={goToInternalJobBoard}
            data-test-id="internal-job-board-nudge-link"
          >
            Go to Internal job board
          </KText> */}
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};
